import { randomId } from './randomId.mjs';
import { isRecord } from './record.mjs';
import { waitUnlessAborted } from './waitUnlessAborted.mjs';

export { retryAfterMs } from './httpClient.mjs';

export const RETRY_MAX_DELAY_MS = 60000;
export const RETRY_READY_STABILITY_MS = 60000;

const modes = new Set(['request', 'connection', 'long-lived-transport']);
const owners = new Set(['shared', 'pi', 'sdk']);
const targets = new Set([
  'model', 'request', 'chat', 'events', 'computer', 'pty', 'desktop-viewer',
  'desktop-control', 'platform', 'mcp', 'service', 'runner', 'local-execution',
]);
const phases = new Set(['attempting', 'scheduled', 'succeeded', 'exhausted', 'blocked', 'cancelled']);
const reasons = new Set([
  'network', 'timeout', 'rate_limited', 'unavailable', 'not_ready',
  'auth_required', 'forbidden', 'closed', 'aborted', 'deadline',
]);
const counter = value => Number.isSafeInteger(value) && value >= 0;
const nonempty = value => typeof value === 'string' && value.trim().length > 0;
const duration = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const baseDelay = value => Number.isInteger(value) && value >= 1000 && value <= 30000;

function isTarget(value) {
  return isRecord(value) && targets.has(value.kind) && nonempty(value.id)
    && (value.accountId === undefined || (counter(value.accountId) && value.accountId > 0))
    && (value.projectId === undefined || (counter(value.projectId) && value.projectId > 0));
}

export function isRetryPolicy(value) {
  return isRecord(value) && counter(value.revision)
    && counter(value.maxRetries) && value.maxRetries <= 10
    && baseDelay(value.baseDelayMs)
    && value.maxDelayMs === RETRY_MAX_DELAY_MS && value.jitter === 'none';
}

export function retryDelayMs(baseDelayMs, retryNumber) {
  if (!baseDelay(baseDelayMs) || !counter(retryNumber) || retryNumber < 1) {
    throw new TypeError('Invalid retry delay arguments');
  }
  // Growth already reaches the cap by retry 7 at the minimum allowed base.
  return Math.min(baseDelayMs * 2 ** Math.min(retryNumber - 1, 6), RETRY_MAX_DELAY_MS);
}

export function isRetryProgress(value) {
  if (!isRecord(value) || !isTarget(value.target) || !nonempty(value.episodeId)
    || !counter(value.generation) || !counter(value.sequence) || !counter(value.policyRevision)
    || !modes.has(value.mode) || !owners.has(value.retryOwner) || !phases.has(value.phase)
    || (value.reason !== null && !reasons.has(value.reason))
    || (value.retryNumber !== null && !counter(value.retryNumber))
    || (value.maxRetries !== null && (!counter(value.maxRetries) || value.maxRetries > 10))) return false;
  if (value.mode === 'long-lived-transport') {
    if (value.maxRetries !== null || value.phase === 'exhausted'
      || !['computer', 'platform'].includes(value.target.kind)) return false;
  } else if (value.retryOwner === 'shared' && value.maxRetries === null) return false;
  if (value.retryOwner === 'shared' && value.retryNumber === null) return false;
  if (value.maxRetries !== null && value.retryNumber !== null && value.retryNumber > value.maxRetries) return false;

  const noSchedule = value.scheduledAt === null && value.nextAttemptAt === null && value.delayMs === null;
  if (value.phase !== 'scheduled') return noSchedule;
  if (noSchedule) return value.retryOwner !== 'shared';
  return duration(value.scheduledAt) && duration(value.nextAttemptAt) && duration(value.delayMs)
    && value.nextAttemptAt === value.scheduledAt + value.delayMs;
}

export function isConnectionState(value) {
  return isRecord(value) && isTarget(value.target) && counter(value.generation) && counter(value.sequence)
    && ['connecting', 'connected', 'disconnected', 'unknown'].includes(value.transport)
    && ['pending', 'ready', 'unknown'].includes(value.readiness)
    && (value.retry === null || isRetryProgress(value.retry))
    && duration(value.observedAt) && isRecord(value.authority)
    && ['local-owner', 'server-observation', 'remote-report'].includes(value.authority.kind)
    && nonempty(value.authority.id) && ['fresh', 'stale', 'unknown'].includes(value.freshness);
}

/** Absolute monotonic targets retain their deadline; the shared wait owns timer chunking. */
async function waitUntil(at, signal) {
  while (!signal.aborted) {
    const remaining = at - performance.now();
    if (remaining <= 0) return true;
    if (!await waitUnlessAborted(remaining, signal)) return false;
  }
  return false;
}

export function createRetryController(options) {
  const mode = options.mode ?? 'request';
  if (!isTarget(options.target) || !modes.has(mode)
    || (mode === 'long-lived-transport' && !['computer', 'platform'].includes(options.target.kind))
    || (options.deadlineAt !== undefined && !duration(options.deadlineAt))) {
    throw new TypeError('Invalid retry controller options');
  }
  const target = Object.freeze({ ...options.target });
  const deadlineAt = options.deadlineAt ?? null;
  let generation = 0;
  let sequence = 0;
  let current = null;
  let stopped = false;
  let tail = Promise.resolve();

  const active = episode => current === episode && !episode.finished && !episode.abort.signal.aborted;

  function emit(episode, phase, reason = null, schedule = null) {
    if (current !== episode || stopped || episode.finished) return;
    options.onProgress?.(Object.freeze({
      target, episodeId: episode.handle.episodeId, generation: episode.handle.generation,
      sequence: ++sequence, policyRevision: episode.policy.revision, mode, retryOwner: 'shared',
      phase, retryNumber: schedule ? episode.retryNumber + 1 : episode.retryNumber,
      maxRetries: mode === 'long-lived-transport' ? null : episode.policy.maxRetries,
      scheduledAt: schedule?.scheduledAt ?? null, nextAttemptAt: schedule?.nextAttemptAt ?? null,
      delayMs: schedule?.delayMs ?? null, reason,
    }));
  }

  function cancel(episode, reason) {
    if (episode.finished || episode.cancelReason !== null) return;
    episode.cancelReason = reason;
    episode.abort.abort();
    emit(episode, 'cancelled', reason);
  }

  async function run(episode) {
    const signal = episode.abort.signal;
    const completion = () => Object.freeze({ outcome: 'cancelled', reason: episode.cancelReason });
    try {
      if (signal.aborted) return completion();
      if (deadlineAt !== null) {
        if (performance.now() >= deadlineAt) {
          cancel(episode, 'deadline');
          return completion();
        }
        void waitUntil(deadlineAt, signal).then(alive => {
          if (alive && active(episode)) cancel(episode, 'deadline');
        });
      }
      let original = true;
      while (active(episode)) {
        if (!original) episode.retryNumber += 1;
        original = false;
        emit(episode, 'attempting');
        if (!active(episode)) return completion();
        if (deadlineAt !== null && performance.now() >= deadlineAt) {
          cancel(episode, 'deadline');
          return completion();
        }
        const readiness = new AbortController();
        let stability = null;
        let readyAt = null;
        let result;
        const context = Object.freeze({
          signal, generation: episode.handle.generation, retryNumber: episode.retryNumber,
          policy: episode.policy, deadlineAt,
          ready() {
            if (!active(episode) || readiness.signal.aborted || readyAt !== null) return;
            if (mode === 'request') throw new TypeError('Request readiness is its successful result');
            if (deadlineAt !== null && performance.now() >= deadlineAt) {
              cancel(episode, 'deadline');
              return;
            }
            readyAt = performance.now();
            const stable = new AbortController();
            stability = stable;
            emit(episode, 'succeeded');
            void waitUntil(readyAt + RETRY_READY_STABILITY_MS, stable.signal).then(alive => {
              if (alive && !stable.signal.aborted && active(episode)) {
                episode.retryNumber = 0;
                emit(episode, 'succeeded');
              }
            });
          },
          notReady() {
            if (!active(episode) || readiness.signal.aborted || readyAt === null) return;
            readyAt = null;
            stability.abort();
            emit(episode, 'attempting', 'not_ready');
          },
        });
        const cancelReadiness = () => {
          readiness.abort();
          stability?.abort();
        };
        signal.addEventListener('abort', cancelReadiness, { once: true });
        try {
          result = await options.attempt(context);
        } finally {
          signal.removeEventListener('abort', cancelReadiness);
          cancelReadiness();
        }
        if (!active(episode)) return completion();
        if (deadlineAt !== null && performance.now() >= deadlineAt) {
          cancel(episode, 'deadline');
          return completion();
        }
        // A disconnect can be delivered before the overdue stability timer after sleep.
        if (readyAt !== null && performance.now() - readyAt >= RETRY_READY_STABILITY_MS) {
          episode.retryNumber = 0;
        }
        if (!isRecord(result)) throw new TypeError('Invalid retry attempt result');
        if (result.outcome === 'succeeded') {
          emit(episode, 'succeeded');
          return Object.freeze({ outcome: 'succeeded', value: result.value });
        }
        if (!reasons.has(result.reason)) throw new TypeError('Invalid retry failure reason');
        if (result.outcome === 'cancelled' && result.reason === 'aborted') {
          cancel(episode, 'aborted');
          return completion();
        }
        if (result.outcome === 'blocked') {
          emit(episode, 'blocked', result.reason);
          return Object.freeze({ outcome: 'blocked', reason: result.reason });
        }
        if (result.outcome !== 'retry') throw new TypeError('Invalid retry attempt outcome');
        if (mode !== 'long-lived-transport' && episode.retryNumber >= episode.policy.maxRetries) {
          emit(episode, 'exhausted', result.reason);
          return Object.freeze({ outcome: 'exhausted', reason: result.reason });
        }
        const hint = result.retryAfterMs ?? 0;
        if (!duration(hint)) throw new TypeError('Invalid retry not-before delay');
        const delayMs = Math.max(retryDelayMs(episode.policy.baseDelayMs, episode.retryNumber + 1), hint);
        const at = performance.now() + delayMs;
        if (!Number.isFinite(at)) throw new TypeError('Retry delay exceeds the scheduling range');
        if (deadlineAt !== null && at >= deadlineAt) {
          cancel(episode, 'deadline');
          return completion();
        }
        const scheduledAt = Date.now();
        emit(episode, 'scheduled', result.reason, { scheduledAt, nextAttemptAt: scheduledAt + delayMs, delayMs });
        if (!await waitUntil(at, signal)) return completion();
      }
      return completion();
    } catch (error) {
      // Cancellation invalidates even a late adapter rejection; live faults still reject done.
      if (signal.aborted) return completion();
      throw error;
    } finally {
      episode.finished = true;
      episode.abort.abort();
    }
  }

  function begin() {
    if (stopped) throw new Error('Retry controller is stopped');
    const policy = options.getPolicy();
    if (!isRetryPolicy(policy)) throw new TypeError('Invalid retry policy');
    const captured = Object.freeze({ ...policy });
    if (current) cancel(current, 'aborted');
    const episode = {
      policy: captured, retryNumber: 0, cancelReason: null, finished: false,
      abort: new AbortController(), handle: null,
    };
    const done = tail.then(() => run(episode));
    episode.handle = Object.freeze({
      episodeId: randomId(), generation: ++generation, done,
    });
    current = episode;
    // Keep cleanup serial even after a failed episode; its public done still rejects.
    tail = done.then(() => undefined, () => undefined);
    if (options.signal?.aborted) cancel(episode, 'aborted');
    return episode.handle;
  }

  function stop() {
    if (!stopped) {
      if (current) cancel(current, 'aborted');
      stopped = true;
      options.signal?.removeEventListener('abort', stop);
    }
    return tail;
  }

  if (!options.signal?.aborted) options.signal?.addEventListener('abort', stop, { once: true });
  return {
    start() {
      if (stopped) throw new Error('Retry controller is stopped');
      return current?.handle ?? begin();
    },
    restart: begin,
    stop,
  };
}
